import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion, animate } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { getPreview } from '@/lib/employeePreview';
import { captureRewardSource, clearRewardSource, subscribeRewards, snapshotRect, reverseDestination, rewardStrength } from '@/lib/rewardBus';
import './RewardFX.css';

const Context = createContext({ display:null, busy:false, impact:0 });
export const useRewardFX = () => useContext(Context);
export const useRewardDisplay = fallback => useRewardFX().display || fallback;
export function RewardLevel({level,prestige=0}) {
  const preference=useReducedMotion();
  const reduced=useRewardFX().reduced ?? preference;
  return <motion.span key={`${prestige}:${level}`} style={{display:'inline-block'}}
    initial={reduced?false:{opacity:.45,scale:.94}} animate={{opacity:1,scale:1}}
    transition={{duration:reduced?0:.28}}>{level??'–'}</motion.span>;
}
const center = r => ({x:r.x+r.width/2,y:r.y+r.height/2});
const number = v => Number(v||0).toLocaleString('de-DE',{maximumFractionDigits:2});
function visibleTarget() {
  return [...document.querySelectorAll('[data-xp-target]')].find(el=>{
    const r=el.getBoundingClientRect();
    return r.width>0 && r.height>0 && r.top>=0 && r.bottom<=window.innerHeight && !el.closest('[inert]') && getComputedStyle(el).visibility!=='hidden';
  });
}

export function RewardFXProvider({ userId, enabled, children }) {
  const initialReduced=useReducedMotion();
  const [reduced,setReduced]=useState(initialReduced);
  useEffect(()=>{
    const media=window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if(!media)return undefined;
    const change=()=>setReduced(media.matches);change();media.addEventListener?.('change',change);
    return()=>media.removeEventListener?.('change',change);
  },[]);
  const [display,setDisplay]=useState(null),[effect,setEffect]=useState(null),[busy,setBusy]=useState(false),[impact,setImpact]=useState(0),[announcement,setAnnouncement]=useState('');
  const generation=useRef(0);
  const route=useLocation(); const routeRef=useRef(route.pathname); routeRef.current=route.pathname;
  useEffect(()=>{
    const gen=++generation.current; let alive=true, fetching=false, cursor=null, timer, animationTimer, running=false;
    const inflight=new Map(), pending=new Map(), origins=new Map(), seen=new Set(), queue=[];
    const active=()=>alive && generation.current===gen;
    const show=p=>{if(active()){setDisplay(p);}};
    setDisplay(null);setEffect(null);setBusy(false);setAnnouncement('');clearRewardSource();
    if(!enabled || getPreview())return()=>{alive=false;};
    const cancelAnimations=new Set();
    const run=()=>{
      if(!active()||running)return;
      const next=queue.shift();
      if(!next){setBusy(false);return;}
      running=true;setBusy(true);
      const {item}=next; const el=visibleTarget(); const target=snapshotRect(el);
      const request=next.request || (item.delta_units<0 && target?{rect:target,route:routeRef.current}:null);
      const valid=request && target && item.delta_units!==0;
      const after=item.after;
      if(!valid){show(after);running=false;run();return;}
      const reverse=item.delta_units<0;
      const remembered=next.request?origins.get(item.claim_id):null;
      const destination=reverseDestination(item,remembered,request,routeRef.current,target);
      const from=reverse?target:request.rect, to=reverse?destination:target;
      const id=item.id;
      show(item.before);
      if(!reverse)origins.set(item.claim_id,{rect:request.rect,route:request.route,at:Date.now(),element:request.element&&typeof WeakRef!=="undefined"?new WeakRef(request.element):null});
      for(const [key,origin] of origins)if(Date.now()-origin.at>120000)origins.delete(key);
      while(origins.size>150)origins.delete(origins.keys().next().value);
      if(!reduced && request.element?.isConnected && request.route===routeRef.current && !reverse){
        const anim=request.element.animate?.([{transform:'scale(1)'},{transform:'scale(1.025)'},{transform:'scale(1)'}],{duration:180});
        if(anim){cancelAnimations.add(anim);anim.onfinish=()=>cancelAnimations.delete(anim);}
      }
      setEffect({id,from,to,reverse,amount:item.delta_units/item.xp_scale,action:item.action,reduced});
      const finish=()=>{
        if(!active())return;
        setEffect(null);setAnnouncement(`${reverse?'−':'+'}${number(Math.abs(item.delta_units/item.xp_scale))} XP`);
        setImpact(v=>v+1);
        const before=item.before, levelChanged=before.level!==after.level||before.prestige!==after.prestige;
        if(reduced){show(after);running=false;run();return;}
        const count=()=>{
          const control=animate(before.cycle_units,after.cycle_units,{duration:.32,onUpdate:value=>{
            if(active())show({...after,cycle_units:value,progress:levelChanged?after.progress:before.progress+(after.progress-before.progress)*Math.min(1,Math.abs((value-before.cycle_units)/(after.cycle_units-before.cycle_units||1)))});
          },onComplete:()=>{cancelAnimations.delete(control);show(after);running=false;run();}});
          cancelAnimations.add(control);
        };
        if(levelChanged){show({...before,progress:reverse?0:1});animationTimer=setTimeout(count,220);}else count();
      };
      animationTimer=setTimeout(finish,reduced?80:1150);
    };
    const schedule=ms=>{clearTimeout(timer);timer=setTimeout(poll,ms);};
    const poll=async()=>{
      if(!active()||fetching)return;
      for(const [id,at] of inflight)if(Date.now()-at>90000)inflight.delete(id);
      if(inflight.size){schedule(400);return;}
      fetching=true;
      for(const [id,p] of pending)if(Date.now()-p.started>90000)pending.delete(id);
      const requested=[...pending.keys()].slice(0,40);
      try{
        const {data}=await api.get('/gamification/reward-feed',{params:{...(cursor!==null?{after:cursor}:{}),operations:requested.join(',')}});
        if(!active())return;
        // A newer action may have started/completed while this read was in flight.
        // Do not consume its ledger row without its confirmed operation receipt.
        if(inflight.size || [...pending.keys()].some(id=>!requested.includes(id))){schedule(300);return;}
        if(data.ready){
          const initial=cursor===null;cursor=data.cursor;
          if(initial){show(data.profile);pending.clear();}
          else{
            for(const item of data.items||[]){
              if(seen.has(item.id))continue;seen.add(item.id);
              const request=pending.get(item.operation_id);
              if(queue.length>=80)queue.splice(0,queue.length);
              queue.push({item,request});
            }
            while(seen.size>1000)seen.delete(seen.values().next().value);
            if(!data.has_more){requested.forEach(id=>pending.delete(id));if(!queue.length&&!running){show(data.profile);setBusy(false);}}
            run();
          }
          schedule(data.has_more?100:pending.size?800:45000);
        }else schedule(800);
      }catch(_){if(active()){if(!pending.size&&!running)setBusy(false);schedule(pending.size?1800:45000);}}
      finally{fetching=false;}
    };
    const unsubscribe=subscribeRewards(request=>{
      if(!active()||cursor===null)return;
      if(request.state==="started"){inflight.set(request.id,request.started);return;}
      inflight.delete(request.id);
      if(request.state==="failed"){schedule(50);return;}
      pending.set(request.id,request);setBusy(true);
      // Bound history without allowing unlimited retained DOM references.
      while(pending.size>40)pending.delete(pending.keys().next().value);
      schedule(300);
    });
    document.addEventListener('click',captureRewardSource,true);document.addEventListener('submit',captureRewardSource,true);
    const focus=()=>{if(!document.hidden)schedule(100);};window.addEventListener('focus',focus);
    poll();
    return()=>{alive=false;clearTimeout(timer);clearTimeout(animationTimer);cancelAnimations.forEach(a=>{if(a.stop)a.stop();else a.cancel?.();});unsubscribe();inflight.clear();pending.clear();queue.length=0;origins.clear();seen.clear();clearRewardSource();document.removeEventListener('click',captureRewardSource,true);document.removeEventListener('submit',captureRewardSource,true);window.removeEventListener('focus',focus);};
  },[userId,enabled,reduced]);
  return <Context.Provider value={{display,busy,impact,reduced}}>{children}
    {enabled&&createPortal(<><div className="reward-layer" aria-hidden="true">{effect&&<RewardAnimationLayer key={effect.id} effect={effect}/>}</div><span className="sr-only" role="status" aria-live="polite" aria-atomic="true">{announcement}</span></>,document.body)}
  </Context.Provider>;
}

export function RewardAnimationLayer({effect}) {
  const {from,to,reverse,amount,action,reduced}=effect;const a=center(from),b=center(to);
  if(reduced)return <span className="reward-reduced" style={{left:b.x,top:b.y}}>{amount>0?'+':'−'}{number(Math.abs(amount))} XP</span>;
  const count=rewardStrength(action);
  return <>
    <motion.div className={`reward-source ${reverse?'reverse':''}`} style={{left:a.x,top:a.y}} initial={{opacity:0,scale:.5}} animate={{opacity:[0,.8,0],scale:[.5,1.4,1.8]}} transition={{duration:.22}}/>
    {Array.from({length:count},(_,i)=>{
      const angle=i*2.399, radius=18+(i%4)*7, bx=a.x+Math.cos(angle)*radius,by=a.y+Math.sin(angle)*radius;
      const core=i===0;const cx=Math.max(16,Math.min(window.innerWidth-16,(a.x+b.x)/2+Math.sin(angle)*60)),cy=Math.max(20,Math.min(a.y,b.y)-Math.min(100,Math.abs(a.x-b.x)*.12)-i%3*12);
      return <motion.span key={i} className={`reward-particle ${core?'xp-core':i%3===0?'gold-shard':'gold-spark'} ${reverse?'reverse':''}`}
        style={{left:0,top:0}} initial={{x:a.x,y:a.y,opacity:0,scale:.4}}
        animate={{x:[a.x,bx,cx,b.x],y:[a.y,by,cy,b.y],opacity:[0,1,1,0],scale:[.4,core?1.15:1,.85,.2],rotate:core?0:[0,70+i*9,180+i*12,240]}}
        transition={{duration:core?.95:.72,delay:core?0:i*.012,times:[0,.16,.55,1],ease:'easeInOut'}}>
        {core&&<b>{amount>0?'+':'−'}{number(Math.abs(amount))} XP</b>}
      </motion.span>;
    })}
    <motion.div className={`reward-impact ${reverse?'reverse':''} ${action==='closed_won'?'close-impact':''}`} style={{left:b.x,top:b.y}}
      initial={{opacity:0,scale:.3}} animate={{opacity:[0,.9,0],scale:[.3,1,1.7]}} transition={{delay:.82,duration:.3}}/>
  </>;
}

export function GlobalXPIndicator() {
  const {display,impact}=useRewardFX();const [needed,setNeeded]=useState(false);
  useEffect(()=>{
    const check=()=>{const header=document.querySelector('.cockpit-header');setNeeded(window.innerWidth<768 || header?.dataset.collapsed==='true' || !header);};
    const observer=new MutationObserver(check);observer.observe(document.body,{subtree:true,attributes:true,attributeFilter:['data-collapsed']});
    window.addEventListener('resize',check);check();return()=>{observer.disconnect();window.removeEventListener('resize',check);};
  },[]);
  if(!needed||!display)return null;
  return <Link to="/m/career" data-xp-target className="global-xp-indicator" aria-label={`Karriere, Level ${display.level}`}>
    <motion.span key={impact} animate={{opacity:[.65,1]}}><b>LVL <RewardLevel level={display.level} prestige={display.prestige}/></b><span>{number(display.cycle_units/display.xp_scale)} / {number(display.next_level_units/display.xp_scale)} XP</span></motion.span>
    <span className="reward-progress" role="progressbar" aria-label="Level-Fortschritt" aria-valuenow={Math.round(display.progress*100)} aria-valuemin={0} aria-valuemax={100}><i style={{transform:`scaleX(${display.progress})`}}/></span>
  </Link>;
}
